import React, { useState } from "react";

import { Modal } from "react-bootstrap";
import {
  Controller,
  FieldValues,
  FormProvider,
  useForm,
} from "react-hook-form";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "primereact/button";

import { listAgency } from "@/_services/serviceService";
import AdditionalPropertyForm from "@/common/components/form/dwell/AdditionalPropertyForm";
import CharacteristicsPropertyForm from "@/common/components/form/dwell/CharacteristicsPropertyForm";
import { EquipmentPropertyForm } from "@/common/components/form/dwell/EquipmentPropertyForm.tsx";
import InformationsPropertyForm from "@/common/components/form/dwell/InformationsPropertyForm";
import { ErrorForm } from "@/common/components/form/ErrorForm.tsx";
import Loader from "@/common/components/Loader.tsx";
import SelectAgency from "@/common/components/SelectAgency";
import UploadDocumentForm from "@/common/components/UploadDocumentForm.tsx";
import { Permissions } from "@/common/enums/sharedEnums.ts";
import { Agency } from "@/common/interfaces";
import { DwellingEquipmentType } from "@/modules/dwelling/enums";
import { IDwelling } from "@/modules/dwelling/reducers";
import * as Services from "@/modules/dwelling/services";
import {
  addDwellingExpress,
  getDwellingInfos,
} from "@/modules/dwelling/services";
import { RootState } from "@/store";

interface IModalDwellingExpressProps {
  id?: string;
  show: boolean;
  onHide: () => void;
  onSubmit: (dwelling: FieldValues) => void;
}
const ModalDwellingExpress: React.FC<IModalDwellingExpressProps> = ({
  show,
  onHide,
  onSubmit,
  id,
}) => {
  const { data, isLoading } = useQuery<IDwelling, Error>(
    ["dwelling", id],
    () => getDwellingInfos(id),
    {
      enabled: !!id,
      staleTime: Infinity,
    }
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      {id && isLoading ? (
        <Modal.Body
          style={{
            height: "calc(100vh - 200px)",
          }}
          className={"d-flex flex-column gap-3"}
        >
          <Loader />
        </Modal.Body>
      ) : (
        <ContentForm id={id} onSubmit={onSubmit} data={data} onHide={onHide} />
      )}
    </Modal>
  );
};

const ContentForm = ({
  onSubmit,
  data,
  onHide,
  id,
}: {
  id?: string;
  onSubmit: (dwelling: FieldValues) => void;
  data?: IDwelling;
  onHide: () => void;
}) => {
  const queryClient = useQueryClient();

  const airConditioner =
    data &&
    data.equipments.find(
      (e) => e.type === DwellingEquipmentType.AIR_CONDITIONER
    )
      ? data.equipments.find(
          (e) => e.type === DwellingEquipmentType.AIR_CONDITIONER
        )
      : undefined;

  const heater =
    data &&
    data?.equipments.find((e) => e.type === DwellingEquipmentType.HEATER);

  const hotWater =
    data &&
    data?.equipments.find((e) => e.type === DwellingEquipmentType.HOT_WATER);

  const defaultValues = data
    ? {
        ...data,
        id: id,
        equipments: data.equipments
          ? {
              airConditioner: {
                isCollective: airConditioner?.isCollective,
                isInstalled: typeof airConditioner?.isCollective === "boolean",
              },
              heater: {
                isCollective: heater?.isCollective,
                energies: heater?.energies ? heater?.energies[0] : undefined,
              },
              hotWater: {
                isCollective: hotWater?.isCollective,
                energies: hotWater?.energies
                  ? hotWater?.energies[0]
                  : undefined,
              },
            }
          : undefined,
      }
    : {
        category: { value: "APT", label: "Appartement" },
        files: [],
        equipments: [],
        isCondominium: false,
        isFurnished: false,
        isSociety: false,
      };

  const methods = useForm<FieldValues>({
    defaultValues,
  });

  const [dwellingDocuments, setDwellingDocuments] = useState<Array<any>>(
    data?.files || []
  );
  const referentials = useSelector(
    (state: RootState) => state.referentialReducer.referentials
  );

  const mutationDwellingFiles = useMutation<any>({
    mutationFn: (filesToAdd: any) =>
      Services.uploadDwellingFiles(id as string, filesToAdd),
    onError: (error: any) => {
      console.log("Error ModalDwellingExpress :", error);
      toast.error(
        "Impossible d'ajouter les fichiers.\nVeuillez contracter votre administrateur système."
      );
    },
    onSuccess: async () => {
      toast.success("Fichiers ajoutés");
      console.log("Files added");
    },
  });

  const mutationDwelling = useMutation<FieldValues, any, any, any>({
    mutationFn: (dataForm: FieldValues) => {
      console.log(dataForm.equipments);
      const equipments = [
        {
          id: airConditioner?.id,
          type: DwellingEquipmentType.AIR_CONDITIONER,
          isCollective: dataForm.equipments.airConditioner.isInstalled
            ? (dataForm.equipments?.airConditioner?.isCollective ?? false)
            : undefined,
        },
        {
          id: heater?.id,
          type: DwellingEquipmentType.HEATER,
          isCollective: dataForm.equipments?.heater?.isCollective ?? false,
          energies: dataForm.equipments?.heater?.energies
            ? [dataForm.equipments.heater.energies]
            : [],
        },
        {
          id: hotWater?.id,
          type: DwellingEquipmentType.HOT_WATER,
          isCollective: dataForm.equipments?.hotWater?.isCollective ?? false,
          energies: dataForm.equipments?.hotWater?.energies
            ? [dataForm.equipments.hotWater.energies]
            : [],
        },
      ];
      const dataResolved = {
        ...dataForm,
        lessorName: dataForm.lessorId
          ? dataForm.lessorId.label
          : dataForm.lessorName,
        lessorId: dataForm.lessorId?.value,
        ADEMENumber: dataForm.ademeNumber,
        equipments,
        buildingId: dataForm.building?.id,
        files: dwellingDocuments,
      } as any;
      if (id) {
        return Services.updateDwelling(dataResolved);
      } else {
        return addDwellingExpress(dataResolved);
      }
    },
    onError: (error) => {
      if (id) {
        console.log("Error ModalDwellingExpress :", error);
        toast.error(
          "Impossible de modifié le bien.\nVeuillez contracter votre administrateur système."
        );
      } else {
        console.log("Error ModalDwellingExpress :", error);
        toast.error(
          "Impossible d'ajouter le bien.\nVeuillez contracter votre administrateur système."
        );
      }
    },
    onSuccess: async (dwelling) => {
      const message = id
        ? "Bien modifié avec succès !"
        : "Bien ajouté avec succès !";
      onSubmit(dwelling);
      toast.success(message);
     if (dwellingDocuments.length > 0) {
        const filesToAdd: Array<any> = dwellingDocuments.filter(
          (documentDwelling) => !("url" in documentDwelling)
        );
        if (filesToAdd.length > 0)
          await mutationDwellingFiles.mutateAsync(filesToAdd);
      }
      id &&
        (await queryClient.invalidateQueries({ queryKey: ["dwelling", id] }));
      onHide();
    },
  });

  const removeFile = (idFile: string | number) => {
    id &&
      Services.removeDwellingFile(id, idFile)
        .then(() => {
          toast.success("Le fichier à été supprimé");
        })
        .catch(() => {
          toast.error("Impossible de supprimé le fichier");
        });
  };

  const {
    data: agencies,
    isLoading: loadingAgencies,
    error: errorAgencies,
  } = useQuery<Agency[], Error>(
    ["listAgency", Permissions.BUILDING_DWELLING_CREATE],
    () => listAgency(Permissions.BUILDING_DWELLING_CREATE)
  );
  const [agency, setAgency] = useState<Agency | undefined>(undefined);
  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(mutationDwelling.mutate)}>
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            Ajouter un bien dans l'agence :&nbsp;
          </Modal.Title>
          <div className="z-40 mt-6">
            <Controller
              control={methods.control}
              name={"agencyId"}
              rules={{
                required: "Veuillez choisir une agence",
              }}
              render={({ field, fieldState }) => (
                <>
                  <SelectAgency
                    agencies={agencies}
                    loading={loadingAgencies}
                    error={errorAgencies}
                    defaultValue={data?.agency?.id}
                    rightValue={Permissions.BUILDING_DWELLING_CREATE}
                    setAgency={(option) => {
                      setAgency(option);
                      field.onChange(option.value);
                    }}
                  />
                  <ErrorForm error={fieldState?.error} />
                </>
              )}
            />
          </div>
        </Modal.Header>
        <Modal.Body className={"d-flex flex-column gap-3 position-relative"}>
          {mutationDwelling.isLoading ? (
            <div
              style={{
                zIndex: 1000,
                backgroundColor: "rgba(255, 255, 255, 0.5)",
              }}
              className={"position-absolute top-0 start-0 w-100 h-100"}
            >
              <Loader />
            </div>
          ) : null}
          <CharacteristicsPropertyForm agency={agency} />
          <InformationsPropertyForm />
          <EquipmentPropertyForm />
          <AdditionalPropertyForm />
          {id ? (
            <div className="bg-container d-flex flex-column gap-3">
              <h3 className="fw-bold main-title" style={{ fontSize: "24px" }}>
                Documents
              </h3>
              <UploadDocumentForm
                documents={dwellingDocuments}
                setDocuments={setDwellingDocuments}
                referentials={referentials}
              />
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button type={"button"} outlined onClick={onHide}>
            Retour
          </Button>
          <Button
            label={id ? "Enregistrer" : "Ajouter"}
            icon={id ? "pi pi-save" : "pi pi-plus"}
            loading={methods.formState.isSubmitting}
            disabled={mutationDwelling.isLoading}
          />
        </Modal.Footer>
      </form>
    </FormProvider>
  );
};

export default ModalDwellingExpress;
